import katex from 'katex';
import { normalizeBacktickWrappedMath } from './latexBackticks.js';

function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function stripBrFromMath(mathContent) {
  return mathContent
    .replace(/<br\s*\/?>/gi, ' \\\\ ') // Replace <br> with LaTeX line break
    .replace(/\s*\\\\\s*\\\\\s*/g, ' \\\\ ') // Collapse multiple line breaks
    .trim();
}

/**
 * Convert aligned-like environments into individual display lines.
 * This improves browser text selection behavior by avoiding table-like math layout.
 */
function extractDisplayLines(mathExpr) {
  const envMatch = mathExpr.match(
    /^\\begin\{(aligned|align\*?|gathered)\}([\s\S]*?)\\end\{\1\}$/
  );
  if (!envMatch) return null;

  const body = envMatch[2];

  // If the body contains nested environments (e.g. pmatrix, bmatrix, cases)
  // that also use \\ as row separators, splitting on \\ would break them.
  // Similarly, \hline and \cline only work inside array-like contexts and
  // would break if extracted into standalone display lines.
  // Fall back to letting KaTeX render the full environment natively.
  if (/\\begin\{/.test(body) || /\\hline|\\cline/.test(body)) return null;

  const lines = body
    .split(/\\\\/)
    .map((line) => line.replace(/&/g, '').trim())
    .filter(Boolean);

  return lines.length > 1 ? lines : null;
}

/**
 * KaTeX spaces are often empty spans with margin-right, which creates visible
 * white holes during text selection. Convert them to selectable spacer nodes.
 */
function makeMspacesSelectable(html) {
  return html.replace(
    /<span class="([^"]*\bmspace\b[^"]*)" style="([^"]*)"><\/span>/g,
    (_m, classes, style) => {
      const mrMatch = style.match(/margin-right\s*:\s*([^;]+)\s*;?/i);
      const mlMatch = style.match(/margin-left\s*:\s*([^;]+)\s*;?/i);
      const mr = mrMatch?.[1]?.trim();
      const ml = mlMatch?.[1]?.trim();

      const cleaned = style
        .replace(/margin-right\s*:\s*[^;]+;?/gi, '')
        .replace(/margin-left\s*:\s*[^;]+;?/gi, '')
        .trim();

      const styleParts = [];
      if (cleaned) styleParts.push(cleaned.replace(/;+$/g, ''));
      styleParts.push('display:inline-block');
      if (ml) styleParts.push(`padding-left:${ml}`);
      if (mr) styleParts.push(`width:${mr}`);

      return `<span class="${classes} katex-select-space" style="${styleParts.join(
        ';'
      )};">&nbsp;</span>`;
    }
  );
}

// Marker for currency dollar signs escaped before math processing
const CURRENCY_DOLLAR = '\x00CURRENCY_DOLLAR\x00';

/**
 * Escape dollar signs used as currency (e.g., $100, $2,500) so they are not
 * treated as LaTeX inline math delimiters. Detects $ followed by digits where
 * the number is not immediately followed by a letter, digit, backslash, or
 * another $ (which would indicate math like $3x$, $30\frac{}{}$, or $2\tan$).
 * Also looks past whitespace for math operators/symbols to avoid escaping
 * expressions like $0 < k$.
 */
function escapeCurrencyDollars(text) {
  return text.replace(
    /(?<!\$)\$(?!\$)(\d+(?:,\d{3})*(?:\.\d+)?)(?![a-zA-Z0-9$\\])(?!\.\d)/g,
    (match, number, offset) => {
      // Check if what follows (past whitespace) is a math operator or symbol
      const afterMatch = text.slice(offset + match.length);
      if (/^\s*[<>=+\-\\^_{}([\]]/.test(afterMatch)) {
        return match; // Likely a math delimiter, not currency
      }
      return CURRENCY_DOLLAR + number;
    }
  );
}

function renderMarksInline(escapedText) {
  // escapedText is already HTML-escaped (safe), so replacing tokens with HTML is fine
  return escapedText.replace(/\b([BMA]\d+)\b/g, (_m, mark) => {
    const cls = mark.toLowerCase(); // b1 / m1 / a1
    return `<span class="mark-badge ${cls}">${mark}</span>`;
  });
}

// Markers for list HTML elements (use null bytes to avoid conflicts with normal text)
const LIST_MARKERS = {
  UL_OPEN: '\x00UL_OPEN\x00',
  UL_CLOSE: '\x00UL_CLOSE\x00',
  OL_OPEN: '\x00OL_OPEN\x00',
  OL_CLOSE: '\x00OL_CLOSE\x00',
  LI_OPEN: '\x00LI_OPEN\x00',
  LI_CLOSE: '\x00LI_CLOSE\x00',
};

/**
 * Convert LaTeX list environments to marker-based lists.
 * Handles \begin{itemize}...\end{itemize} and \begin{enumerate}...\end{enumerate}
 * Uses markers instead of HTML so they survive the escapeHtml step.
 */
function convertListEnvironments(text) {
  let prev;
  let iterations = 0;
  const maxIterations = 10; // Safety limit for deeply nested lists

  do {
    prev = text;
    iterations++;

    // Match itemize without nested list markers (processes innermost first)
    text = text.replace(
      /\\begin\{itemize\}((?:(?!\\begin\{(?:itemize|enumerate)\})[\s\S])*?)\\end\{itemize\}/g,
      (_, content) =>
        LIST_MARKERS.UL_OPEN + convertItems(content) + LIST_MARKERS.UL_CLOSE
    );

    // Match enumerate without nested list markers
    text = text.replace(
      /\\begin\{enumerate\}((?:(?!\\begin\{(?:itemize|enumerate)\})[\s\S])*?)\\end\{enumerate\}/g,
      (_, content) =>
        LIST_MARKERS.OL_OPEN + convertItems(content) + LIST_MARKERS.OL_CLOSE
    );
  } while (text !== prev && iterations < maxIterations);

  return text;
}

/**
 * Convert \item entries within a list to marker-based <li> elements.
 */
function convertItems(content) {
  // Split by \item (word boundary prevents matching \itemize etc.)
  const parts = content.split(/\\item\b\s*/);

  // First element is content before any \item (usually whitespace), skip it
  return parts
    .slice(1)
    .map((item) => LIST_MARKERS.LI_OPEN + item.trim() + LIST_MARKERS.LI_CLOSE)
    .join('\n');
}

/**
 * Convert \begin{tabular} to \begin{array} wrapped in display math.
 * KaTeX supports array but not tabular.
 */
function convertTabularToArray(text) {
  return text.replace(
    /\\begin\{tabular\}(\{[^}]*\})?([\s\S]*?)\\end\{tabular\}/g,
    (_, colSpec, body) => {
      const spec = colSpec
        ? colSpec.replace(/\|/g, '')
        : `{${'c'.repeat((body.match(/&/g) || []).length + 1)}}`;
      return `\\[\\begin{array}${spec}${body}\\end{array}\\]`;
    }
  );
}

/**
 * Replace list markers with actual HTML tags.
 */
function replaceListMarkers(html) {
  return html
    .replace(new RegExp(LIST_MARKERS.UL_OPEN, 'g'), '<ul class="latex-list">')
    .replace(new RegExp(LIST_MARKERS.UL_CLOSE, 'g'), '</ul>')
    .replace(new RegExp(LIST_MARKERS.OL_OPEN, 'g'), '<ol class="latex-list">')
    .replace(new RegExp(LIST_MARKERS.OL_CLOSE, 'g'), '</ol>')
    .replace(new RegExp(LIST_MARKERS.LI_OPEN, 'g'), '<li>')
    .replace(new RegExp(LIST_MARKERS.LI_CLOSE, 'g'), '</li>');
}

function skipWhitespace(text, index) {
  let i = index;
  while (i < text.length && /\s/.test(text[i])) i++;
  return i;
}

function readBracedArgument(text, openBraceIndex) {
  if (text[openBraceIndex] !== '{') {
    return null;
  }

  let depth = 1;
  let i = openBraceIndex + 1;
  let value = '';

  while (i < text.length) {
    const ch = text[i];

    if (ch === '\\' && i + 1 < text.length) {
      value += ch + text[i + 1];
      i += 2;
      continue;
    }

    if (ch === '{') {
      depth += 1;
      value += ch;
      i += 1;
      continue;
    }

    if (ch === '}') {
      depth -= 1;
      if (depth === 0) {
        return { value, nextIndex: i + 1 };
      }
      value += ch;
      i += 1;
      continue;
    }

    value += ch;
    i += 1;
  }

  return null;
}

function appendStructuredLine(out, line) {
  let result = out.replace(/[ \t]+$/g, '');
  if (result && !result.endsWith('\n')) result += '\n';
  if (result && !result.endsWith('\n\n')) result += '\n';
  result += line;
  if (!result.endsWith('\n')) result += '\n';
  return result;
}

/**
 * Convert custom solution macros into readable plain-text labels.
 * Supports balanced braces for nested LaTeX inside macro arguments.
 */
export function normalizeSolutionMacros(text) {
  let out = '';
  let i = 0;

  while (i < text.length) {
    if (text[i] !== '\\') {
      out += text[i];
      i += 1;
      continue;
    }

    if (text.startsWith('\\goal', i)) {
      let j = skipWhitespace(text, i + '\\goal'.length);
      const arg = readBracedArgument(text, j);
      if (arg) {
        out = appendStructuredLine(out, `Goal: ${arg.value.trim()}`);
        i = arg.nextIndex;
        continue;
      }
    }

    if (text.startsWith('\\conclusion', i)) {
      let j = skipWhitespace(text, i + '\\conclusion'.length);
      const arg = readBracedArgument(text, j);
      if (arg) {
        out = appendStructuredLine(out, `Conclusion: ${arg.value.trim()}`);
        i = arg.nextIndex;
        continue;
      }
    }

    if (text.startsWith('\\step', i)) {
      let j = skipWhitespace(text, i + '\\step'.length);
      const stepNumberArg = readBracedArgument(text, j);
      if (stepNumberArg) {
        j = skipWhitespace(text, stepNumberArg.nextIndex);
        const stepTitleArg = readBracedArgument(text, j);
        if (stepTitleArg) {
          const stepNumber = stepNumberArg.value.trim();
          const stepTitle = stepTitleArg.value.trim();
          const stepLabel = stepNumber
            ? `Step ${stepNumber}: ${stepTitle}`
            : `Step: ${stepTitle}`;
          out = appendStructuredLine(out, stepLabel);
          i = stepTitleArg.nextIndex;
          continue;
        }
      }
    }

    // Not a supported macro pattern (or malformed) -> keep original char.
    out += text[i];
    i += 1;
  }

  return out;
}

/**
 * Insert explicit LaTeX spaces (\ ) where meaningful whitespace exists between
 * tokens that LaTeX math mode would otherwise concatenate.
 * Only targets gaps that LaTeX does NOT auto-space (e.g. digit-letter, paren-digit).
 * Spaces around relation/binary operators (=, +, -) are left alone.
 */
function addMathSpacing(seg) {
  return (
    seg
      // digit/close-paren → space → \command: "10 \tan" → "10\ \tan"
      .replace(/([\d)])\s+(\\[a-zA-Z])/g, '$1\\ $2')
      // close-paren → space → digit/letter: ") 10" → ")\ 10"
      .replace(/(\))\s+([\da-zA-Z])/g, '$1\\ $2')
      // letter → space → digit: "their 2.8" → "their\ 2.8"
      .replace(/([a-zA-Z])\s+(\d)/g, '$1\\ $2')
      // digit → space → open-paren: "10 (" → "10\ ("
      .replace(/(\d)\s+(\()/g, '$1\\ $2')
      // digit → space → letter: "10 x" → "10\ x"
      .replace(/(\d)\s+([a-zA-Z])/g, '$1\\ $2')
  );
}

/**
 * Wrap only math-like atoms in $...$ and keep surrounding prose untouched.
 * This avoids sending full English phrases into math mode, where spaces collapse.
 */
function wrapMathAtoms(seg) {
  const atomRe =
    /\\[a-zA-Z]+(?:\{[^{}]*\})*|[a-zA-Z](?:\^\{[^{}]+\}|_\{[^{}]+\})?\([^)]+\)|-?\d+(?:\.\d+)?(?:\s*\/\s*-?\d+(?:\.\d+)?)?/g;

  let out = '';
  let last = 0;

  for (const match of seg.matchAll(atomRe)) {
    const idx = match.index ?? 0;
    const atom = match[0];
    const end = idx + atom.length;
    const prev = idx > 0 ? seg[idx - 1] : '';
    const next = end < seg.length ? seg[end] : '';

    // Skip partial matches inside plain words.
    if (/[a-zA-Z]/.test(prev) || /[a-zA-Z]/.test(next)) continue;

    out += seg.slice(last, idx);
    out += `$${addMathSpacing(atom)}$`;
    last = end;
  }

  out += seg.slice(last);
  return out;
}

/**
 * Extract leading/trailing English text from a math segment and wrap only
 * the math core in $...$. This prevents multi-word English text from losing
 * spaces when rendered in LaTeX math mode.
 *
 * Words of 3+ letters are treated as English text. Title-case 2-letter words
 * (e.g. "If", "To") are also extracted. 2-letter ALL-CAPS tokens like "BC"
 * stay in math mode since they're typically geometric labels.
 */
function wrapMathSegments(seg) {
  let leading = '';
  let trailing = '';
  let core = seg;

  // Leading: first word must be 3+ letters (with optional colon/comma) or title-case 2-letter.
  // Subsequent words: any 2+ letters. Each word followed by whitespace.
  const leadMatch = core.match(
    /^((?:[a-zA-Z]{3,}[,:;]?|[A-Z][a-z])\s+(?:(?:[a-zA-Z]{2,}[,:;]?)\s+)*)/
  );
  if (leadMatch) {
    let captured = leadMatch[1];
    // Trim back trailing 2-letter ALL-UPPERCASE words (math labels like BC, AC)
    const backMatch = captured.match(/(\s+[A-Z]{2}\s+)$/);
    if (backMatch) {
      captured = captured.slice(0, captured.length - backMatch[1].length + 1);
    }
    leading = captured;
    core = core.slice(leading.length);
  }

  // Trailing: 3+ letter words preceded by whitespace
  const trailMatch = core.match(/(\s+[a-zA-Z]{3,})+$/);
  if (trailMatch) {
    trailing = trailMatch[0];
    core = core.slice(0, core.length - trailing.length);
  }

  core = core.trim();
  if (!core) return seg;

  return leading + `$${addMathSpacing(core)}$` + trailing;
}

/**
 * Preprocess marking scheme content that contains bare LaTeX (no delimiters).
 * Wraps math segments in $...$ so the renderer can process them.
 */
export function preprocessMarkSchemeContent(content) {
  if (!content) return content;

  const s = String(content).trim();
  if (!s) return content;

  // Already has math delimiters → return unchanged
  if (/\$|\\\(|\\\[/.test(s)) return s;

  // Detect LaTeX indicators
  const hasLatex = /\\[a-zA-Z]+/.test(s);
  const hasTrig =
    /(?<!\\)\b(?:sin|cos|tan|sec|csc|cot|arcsin|arccos|arctan|sinh|cosh|tanh|ln|log)\s*\(/.test(
      s
    );
  const hasMathExpr = /[=^_{}]|\d+\.\d+|\d+\s*\/\s*[\d\\]/.test(s);

  if (!hasLatex && !hasTrig && !hasMathExpr) return s;

  return s
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return line;

      // Convert bare trig function names to LaTeX commands
      let processed = trimmed.replace(
        /(?<!\\)\b(sin|cos|tan|sec|csc|cot|arcsin|arccos|arctan|sinh|cosh|tanh|ln|log)\s*\(/g,
        '\\$1('
      );

      // Split on marking scheme text connectors
      const parts = processed.split(/\b(or|and|oe|cao|isw|awrt)\b/gi);

      const wrapped = parts.map((part, i) => {
        // Odd indices are the captured connectors
        if (i % 2 === 1) return part;

        const seg = part.trim();
        if (!seg) return part;

        // Check if this segment contains math-like content
        const isMath = /\\[a-zA-Z]|[=^_{}]|\d/.test(seg);
        if (!isMath) return part;

        // Mixed prose + math should keep prose outside math mode.
        const hasLongProse =
          /\b(?:[a-z]{2,}|[A-Z][a-z]+)\s+(?:[a-z]{2,}|[A-Z][a-z]+)\b/.test(seg);
        return hasLongProse ? wrapMathAtoms(seg) : wrapMathSegments(seg);
      });

      return wrapped.join(' ').trim();
    })
    .join('\n');
}

/**
 * Renders LaTeX to HTML without throwing errors.
 * Supports all four delimiter formats:
 * - $$...$$ for display math
 * - \[...\] for display math
 * - $...$ for inline math
 * - \(...\) for inline math
 */
export function renderLatexToHtml(input) {
  if (!input) return '';

  let s = String(input).replace(/\\n/g, '\n');

  // remove env wrappers
  s = s.replace(/\\begin\{solution\}/g, '').replace(/\\end\{solution\}/g, '');
  s = normalizeSolutionMacros(s);

  // make \textbf{B1} become plain "B1" so it can be badge-rendered
  s = s.replace(/\\textbf\{([^}]*)\}/g, '$1');

  s = normalizeBacktickWrappedMath(s);

  // Convert tabular to array (KaTeX supports array but not tabular)
  s = convertTabularToArray(s);

  // Convert list environments to markers (before escaping)
  s = convertListEnvironments(s);

  // Escape currency dollar signs (e.g., $100) before math delimiter matching
  s = escapeCurrencyDollars(s);

  // Combined regex: order matters - $$...$$ must come before $...$ to avoid partial matches
  // Groups: 1 = $$...$$ (display), 2 = \[...\] (display), 3 = $...$ (inline), 4 = \(...\) (inline)
  // Group 5+6 = \begin{env}...\end{env} (display) - math environments like align*, cases, etc.
  const re =
    /\$\$([\s\S]*?)\$\$|\\\[([\s\S]*?)\\\]|\$([^$\n]+?)\$|\\\(([\s\S]*?)\\\)|\\begin\{([a-zA-Z*]+)\}([\s\S]*?)\\end\{\5\}/g;

  let out = '';
  let last = 0;

  for (const match of s.matchAll(re)) {
    const idx = match.index ?? 0;

    // Plain text segment before math
    const before = s.slice(last, idx);
    const escaped = escapeHtml(before).replace(/\n/g, '<br/>');
    out += renderMarksInline(escaped);

    // Math segment - determine display mode and extract content
    const isDisplay = match[1] != null || match[2] != null || match[5] != null;
    let rawExpr;
    if (match[5] != null) {
      // \begin{env}...\end{env} - pass the full environment to KaTeX
      rawExpr = `\\begin{${match[5]}}${match[6]}\\end{${match[5]}}`;
    } else {
      rawExpr = match[1] ?? match[2] ?? match[3] ?? match[4] ?? '';
    }

    const expr = stripBrFromMath(rawExpr);
    const displayLines = isDisplay ? extractDisplayLines(expr) : null;

    if (displayLines) {
      const linesHtml = displayLines
        .map((line) =>
          katex.renderToString(line, {
            displayMode: true,
            throwOnError: false,
            output: 'html',
          })
        )
        .join('');
      out += `<div class="katex-multiline">${linesHtml}</div>`;
    } else {
      out += katex.renderToString(expr, {
        displayMode: isDisplay,
        throwOnError: false,
        output: 'html',
      });
    }

    last = idx + match[0].length;
  }

  // Remaining plain text after last math
  const tail = s.slice(last);
  const escapedTail = escapeHtml(tail).replace(/\n/g, '<br/>');
  out += renderMarksInline(escapedTail);

  // Replace list markers and currency markers with actual characters
  out = replaceListMarkers(makeMspacesSelectable(out));
  return out.replace(new RegExp(CURRENCY_DOLLAR, 'g'), '$');
}

/**
 * Same renderer as renderLatexToHtml, but also collects KaTeX errors.
 * Supports all four delimiter formats:
 * - $$...$$ for display math
 * - \[...\] for display math
 * - $...$ for inline math
 * - \(...\) for inline math
 *
 * Returns: { html, errors }
 */
export function renderLatexToHtmlWithErrors(input) {
  const errors = [];
  if (!input) return { html: '', errors };

  let s = String(input).replace(/\\n/g, '\n');

  // remove env wrappers
  s = s.replace(/\\begin\{solution\}/g, '').replace(/\\end\{solution\}/g, '');
  s = normalizeSolutionMacros(s);

  // make \textbf{B1} become plain "B1" so it can be badge-rendered
  s = s.replace(/\\textbf\{([^}]*)\}/g, '$1');

  s = normalizeBacktickWrappedMath(s);

  // Convert tabular to array (KaTeX supports array but not tabular)
  s = convertTabularToArray(s);

  // Convert list environments to markers (before escaping)
  s = convertListEnvironments(s);

  // Escape currency dollar signs (e.g., $100) before math delimiter matching
  s = escapeCurrencyDollars(s);

  // Combined regex: order matters - $$...$$ must come before $...$ to avoid partial matches
  // Groups: 1 = $$...$$ (display), 2 = \[...\] (display), 3 = $...$ (inline), 4 = \(...\) (inline)
  // Group 5+6 = \begin{env}...\end{env} (display) - math environments like align*, cases, etc.
  const re =
    /\$\$([\s\S]*?)\$\$|\\\[([\s\S]*?)\\\]|\$([^$\n]+?)\$|\\\(([\s\S]*?)\\\)|\\begin\{([a-zA-Z*]+)\}([\s\S]*?)\\end\{\5\}/g;

  let out = '';
  let last = 0;

  for (const match of s.matchAll(re)) {
    const idx = match.index ?? 0;

    // Plain text before math (same as original)
    const before = s.slice(last, idx);
    const escaped = escapeHtml(before).replace(/\n/g, '<br/>');
    out += renderMarksInline(escaped);

    // Math segment with error detection
    const isDisplay = match[1] != null || match[2] != null || match[5] != null;
    let rawExpr;
    if (match[5] != null) {
      // \begin{env}...\end{env} - pass the full environment to KaTeX
      rawExpr = `\\begin{${match[5]}}${match[6]}\\end{${match[5]}}`;
    } else {
      rawExpr = match[1] ?? match[2] ?? match[3] ?? match[4] ?? '';
    }
    const expr = stripBrFromMath(rawExpr);
    const displayLines = isDisplay ? extractDisplayLines(expr) : null;

    try {
      if (displayLines) {
        const linesHtml = displayLines
          .map((line) =>
            katex.renderToString(line, {
              displayMode: true,
              throwOnError: true, // <-- detect invalid LaTeX
              output: 'html',
            })
          )
          .join('');
        out += `<div class="katex-multiline">${linesHtml}</div>`;
      } else {
        out += katex.renderToString(expr, {
          displayMode: isDisplay,
          throwOnError: true, // <-- detect invalid LaTeX
          output: 'html',
        });
      }
    } catch (e) {
      const msg =
        (e && (e.message || (e.toString && e.toString()))) ||
        'Invalid LaTeX expression';
      errors.push(msg);

      // Render a readable fallback in the preview
      const wrapped = isDisplay ? `\\[${rawExpr}\\]` : `\\(${rawExpr}\\)`;

      out += `<span class="katex-error">${escapeHtml(wrapped)}</span>`;
    }

    last = idx + match[0].length;
  }

  // Remaining tail text (same as original)
  const tail = s.slice(last);
  const escapedTail = escapeHtml(tail).replace(/\n/g, '<br/>');
  out += renderMarksInline(escapedTail);

  // Replace list markers and currency markers with actual characters
  out = replaceListMarkers(makeMspacesSelectable(out));
  return { html: out.replace(new RegExp(CURRENCY_DOLLAR, 'g'), '$'), errors };
}
